import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Megaphone, Send, Activity, RefreshCw, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function NotificationsTab() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [bcForm, setBcForm] = useState({ title: "", body: "" });
  const [bcSending, setBcSending] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [testingToken, setTestingToken] = useState(null);
  // Targeted notification (one or more users)
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState([]); // [{user_id, name, phone, email, role, devices}]
  const [targetedForm, setTargetedForm] = useState({ title: "", body: "" });
  const [targetedSending, setTargetedSending] = useState(false);

  const loadBroadcasts = () => api.get("/admin/notifications/broadcasts").then(({ data }) => setBroadcasts(data));
  const loadPushStatus = () => api.get("/admin/notifications/push-status")
    .then(({ data }) => setPushStatus(data))
    .catch((err) => toast.error(err.response?.data?.detail || "فشل تحميل حالة الإشعارات"));
  const refreshPushStatus = () => {
    setPushLoading(true);
    loadPushStatus().finally(() => setPushLoading(false));
  };
  const sendTestPush = (suffix) => {
    setTestingToken(suffix);
    api.post("/admin/notifications/test-push", { token_suffix: suffix.replace(/^\.\.\./, "") })
      .then(({ data }) => {
        if (data.ok) {
          toast.success(`✓ تم إرسال الإشعار التجريبي · ${data.message_id?.slice(-12)}`);
        } else {
          const d = data.debug;
          const dbg = d ? `\n\nDEBUG:\nurl: ${d.url}\nproject: ${d.project_id}\ntoken: ${d.token_first20}...${d.token_last8} (len=${d.token_len})\nstatus: ${d.status}\nserver: ${d.resp_server || "(none)"}\nvia: ${d.resp_via || "(none)"}\nwww-auth: ${d.resp_www_auth || "(none)"}\nraw: ${(d.resp_text || "").slice(0, 200)}` : "";
          toast.error(`✗ فشل: ${data.error}${dbg}`, { duration: 30000, style: { maxWidth: 700 } });
          // Also log to console for easy copy
          console.error("TEST-PUSH FAILED", data);
        }
      })
      .catch((err) => toast.error(err.response?.data?.detail || "فشل إرسال الإشعار التجريبي"))
      .finally(() => setTestingToken(null));
  };
  const runTokenProbe = () => {
    toast.info("جاري التشخيص الشامل...");
    api.get("/admin/notifications/token-probe")
      .then(({ data }) => {
        console.log("TOKEN-PROBE RESULT:", data);
        const ti = data.tokeninfo_from_google || {};
        const m1 = data.fcm_via_requests || {};
        const m2 = data.fcm_via_authorized_session || {};
        const m3 = data.fcm_via_urllib || {};
        const summary = `TOKEN: ${data.token_first20}...${data.token_last8} len=${data.token_len}\n\nGoogle says about token:\n  email: ${ti.email || "?"}\n  aud: ${ti.aud || "?"}\n  expires_in: ${ti.expires_in || "?"}s\n  scopes: ${(ti.scope || "").slice(0, 250)}\n\nFCM call via 3 methods:\n  requests: ${m1.status || m1.error}\n  AuthorizedSession: ${m2.status || m2.error}\n  urllib: ${m3.status || m3.error}`;
        toast.info(summary, { duration: 60000, style: { maxWidth: 900, whiteSpace: "pre-wrap" } });
      })
      .catch((err) => toast.error(err.response?.data?.detail || "فشل التشخيص"));
  };

  const sendBroadcast = async () => {
    if (!bcForm.title.trim() || !bcForm.body.trim()) {
      toast.error("اكتب عنوان ونص الإشعار");
      return;
    }
    setBcSending(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", bcForm);
      toast.success(`تم الإرسال إلى ${data.customers} عميل (${data.devices} جهاز) ✓`);
      setBcForm({ title: "", body: "" });
      loadBroadcasts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل إرسال الإشعار");
    } finally {
      setBcSending(false);
    }
  };

  // Debounced user search for targeted notifications
  useEffect(() => {
    const q = userSearchQuery.trim();
    if (!q) { setUserSearchResults([]); return; }
    setUserSearchLoading(true);
    const t = setTimeout(() => {
      api.get(`/admin/users/search?q=${encodeURIComponent(q)}&limit=20`)
        .then(({ data }) => setUserSearchResults(data || []))
        .catch(() => setUserSearchResults([]))
        .finally(() => setUserSearchLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [userSearchQuery]);

  const isSelected = (id) => selectedTargets.some((u) => u.user_id === id);
  const toggleTarget = (u) => setSelectedTargets((prev) =>
    prev.some((x) => x.user_id === u.user_id) ? prev.filter((x) => x.user_id !== u.user_id) : [...prev, u]
  );
  const selectAllResults = () => setSelectedTargets((prev) => {
    const ids = new Set(prev.map((x) => x.user_id));
    return [...prev, ...userSearchResults.filter((u) => !ids.has(u.user_id))];
  });

  const sendTargetedNotification = async () => {
    if (selectedTargets.length === 0) { toast.error("اختر مستخدماً واحداً على الأقل"); return; }
    if (!targetedForm.title.trim() || !targetedForm.body.trim()) {
      toast.error("اكتب عنوان ونص الإشعار"); return;
    }
    setTargetedSending(true);
    try {
      const { data } = await api.post("/admin/notifications/send-to-user", {
        user_ids: selectedTargets.map((u) => u.user_id),
        title: targetedForm.title.trim(),
        body: targetedForm.body.trim(),
      });
      toast.success(`✓ تم إرسال الإشعار إلى ${data.sent} مستخدم (${data.devices} جهاز)`);
      setTargetedForm({ title: "", body: "" });
      setSelectedTargets([]);
      setUserSearchQuery("");
      setUserSearchResults([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل إرسال الإشعار");
    } finally {
      setTargetedSending(false);
    }
  };

  useEffect(() => { loadBroadcasts(); loadPushStatus(); }, []);

  return (
    <>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="jh-card p-6" data-testid="broadcast-form">
          <h2 className="font-bold text-lg text-[#1A1A1A] flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#E05D3A]" />
            إرسال إشعار لجميع العملاء
          </h2>
          <p className="text-xs text-[#6B6B6B] mt-1 mb-4">
            يصل الإشعار لكل العملاء داخل التطبيق + إشعار Push على أجهزتهم المسجّلة (لا يشمل المناديب أو أصحاب المطاعم).
          </p>
          <div className="space-y-4">
            <div>
              <Label>عنوان الإشعار</Label>
              <Input
                value={bcForm.title}
                onChange={(e) => setBcForm({ ...bcForm, title: e.target.value })}
                placeholder="مثال: خصم 30% اليوم فقط 🎉"
                className="mt-1.5"
                maxLength={60}
                data-testid="broadcast-title-input"
              />
            </div>
            <div>
              <Label>نص الإشعار</Label>
              <Textarea
                value={bcForm.body}
                onChange={(e) => setBcForm({ ...bcForm, body: e.target.value })}
                placeholder="مثال: استخدم كود JAYEK30 واحصل على خصم 30% على جميع المطاعم"
                className="mt-1.5"
                rows={3}
                maxLength={180}
                data-testid="broadcast-body-input"
              />
            </div>
            {(bcForm.title || bcForm.body) && (
              <div className="rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-3 flex gap-3 items-start" data-testid="broadcast-preview">
                <div className="w-9 h-9 rounded-lg bg-[#E05D3A] flex items-center justify-center flex-shrink-0">
                  <Megaphone className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1A1A1A] truncate">{bcForm.title || "عنوان الإشعار"}</p>
                  <p className="text-xs text-[#6B6B6B] line-clamp-2">{bcForm.body || "نص الإشعار"}</p>
                </div>
              </div>
            )}
            <Button
              onClick={sendBroadcast}
              disabled={bcSending || !bcForm.title.trim() || !bcForm.body.trim()}
              className="w-full rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white"
              data-testid="broadcast-send-btn"
            >
              {bcSending ? (
                <><Loader2 className="w-4 h-4 me-2 animate-spin" /> جاري الإرسال...</>
              ) : (
                <><Send className="w-4 h-4 me-2" /> إرسال الآن لجميع العملاء</>
              )}
            </Button>
          </div>
        </div>

        <div className="jh-card p-6" data-testid="broadcast-history">
          <h2 className="font-bold text-lg text-[#1A1A1A] mb-4">سجل الإشعارات المرسلة ({broadcasts.length})</h2>
          {broadcasts.length === 0 ? (
            <p className="text-sm text-[#6B6B6B] text-center py-8">لم يتم إرسال أي إشعار بعد</p>
          ) : (
            <div className="space-y-3 max-h-[480px] overflow-y-auto pe-1">
              {broadcasts.map((b) => (
                <div key={b.id} className="rounded-xl border border-[#EAEAEA] p-3" data-testid={`broadcast-item-${b.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-bold text-[#1A1A1A]">{b.title}</p>
                    <span className="text-[11px] text-[#9B9B9B] whitespace-nowrap">
                      {new Date(b.created_at).toLocaleString("ar-SA", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                  <p className="text-xs text-[#6B6B6B] mt-1">{b.body}</p>
                  <p className="text-[11px] text-[#9B9B9B] mt-2">
                    وصل إلى {b.customers} عميل · {b.devices} جهاز Push
                  </p>
                  {Array.isArray(b.push_errors) && b.push_errors.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-[11px] text-[#E05D3A] cursor-pointer">
                        {b.push_errors.length} خطأ في الإرسال (اضغط للعرض)
                      </summary>
                      <ul className="text-[10px] text-[#9B9B9B] mt-1 space-y-0.5 ps-3 list-disc">
                        {b.push_errors.slice(0, 8).map((e, i) => (
                          <li key={i} className="break-all">{e}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Targeted notification - send to a specific user */}
        <div className="jh-card p-6" data-testid="targeted-notify-card">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-5 h-5 text-[#E05D3A]" />
            <h2 className="font-bold text-lg text-[#1A1A1A]">إرسال إشعار لأشخاص محددين</h2>
          </div>

          <div className="space-y-4">
            {/* Search */}
            <div>
              <label className="block text-xs font-bold text-[#3D3D3D] mb-1.5">
                ابحث عن المستخدمين (بالاسم / الجوال / الإيميل) — يمكنك تحديد أكثر من شخص
              </label>
              <Input
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                placeholder="مثال: أحمد أو 0555 أو ahmed@..."
                className="jh-input"
                data-testid="user-search-input"
              />
              {userSearchLoading && (
                <p className="text-[11px] text-[#9B9B9B] mt-1 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> جاري البحث...
                </p>
              )}
            </div>

            {/* Results list */}
            {userSearchResults.length > 0 && (
              <div className="border border-[#EAEAEA] rounded-xl divide-y divide-[#EAEAEA] max-h-[280px] overflow-y-auto" data-testid="user-search-results">
                <div className="px-3 py-2 flex items-center justify-between bg-[#FAFAFA]">
                  <span className="text-[11px] text-[#6B6B6B]">{userSearchResults.length} نتيجة · اضغط لتحديد أكثر من مستخدم</span>
                  <button onClick={selectAllResults} className="text-[11px] font-bold text-[#E05D3A] hover:underline" data-testid="select-all-results-btn">
                    تحديد الكل
                  </button>
                </div>
                {userSearchResults.map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => toggleTarget(u)}
                    aria-pressed={isSelected(u.user_id)}
                    className={`w-full text-start px-3 py-2.5 transition-colors flex items-center justify-between gap-2 ${isSelected(u.user_id) ? "bg-[#FFF4F0]" : "hover:bg-[#FAFAFA]"}`}
                    data-testid={`user-result-${u.user_id}`}
                  >
                    <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isSelected(u.user_id) ? "bg-[#E05D3A] border-[#E05D3A]" : "border-[#C9C9C9] bg-white"}`}>
                      {isSelected(u.user_id) && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-[#1A1A1A] truncate">
                        {u.name || "(بدون اسم)"}
                        <span className={`ms-2 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                          u.role === "admin" ? "bg-purple-100 text-purple-700" :
                          u.role === "driver" ? "bg-blue-100 text-blue-700" :
                          u.role === "owner" ? "bg-amber-100 text-amber-700" :
                          "bg-green-100 text-green-700"
                        }`}>
                          {u.role === "admin" ? "أدمن" : u.role === "driver" ? "سائق" : u.role === "owner" ? "مطعم" : "عميل"}
                        </span>
                      </p>
                      <p className="text-[11px] text-[#6B6B6B] truncate">
                        {u.phone || u.email || u.user_id}
                      </p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap ${u.devices > 0 ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {u.devices} جهاز
                    </span>
                  </button>
                ))}
              </div>
            )}
            {userSearchQuery.trim() && !userSearchLoading && userSearchResults.length === 0 && (
              <p className="text-xs text-[#9B9B9B] text-center py-4">لم يتم العثور على مستخدمين مطابقين</p>
            )}

            {/* Selected users */}
            {selectedTargets.length > 0 && (
              <div className="rounded-xl bg-[#FFF4F0] border border-[#FFD8C9] p-3" data-testid="selected-target-user">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-sm font-bold text-[#1A1A1A]" data-testid="selected-targets-count">
                    المستهدفون: {selectedTargets.length} مستخدم · {selectedTargets.reduce((n, u) => n + (u.devices || 0), 0)} جهاز
                  </p>
                  <button
                    onClick={() => setSelectedTargets([])}
                    className="text-[11px] text-[#E05D3A] font-bold px-2 py-1 rounded-full hover:bg-white"
                    data-testid="clear-target-user-btn"
                  >
                    مسح الكل
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedTargets.map((u) => (
                    <span key={u.user_id} className="inline-flex items-center gap-1 bg-white border border-[#FFD8C9] rounded-full ps-2.5 pe-1 py-0.5 text-[11px] font-semibold text-[#1A1A1A]" data-testid={`target-chip-${u.user_id}`}>
                      {u.name || u.phone || u.email || u.user_id}
                      <button onClick={() => toggleTarget(u)} className="w-4 h-4 rounded-full hover:bg-[#FFF4F0] flex items-center justify-center" aria-label="إزالة" data-testid={`remove-target-${u.user_id}`}>
                        <XCircle className="w-3.5 h-3.5 text-[#E05D3A]" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Title & Body */}
            <div>
              <label className="block text-xs font-bold text-[#3D3D3D] mb-1.5">عنوان الإشعار</label>
              <Input
                value={targetedForm.title}
                onChange={(e) => setTargetedForm({ ...targetedForm, title: e.target.value })}
                placeholder="مثال: عرض خاص لك 🎁"
                maxLength={65}
                className="jh-input"
                data-testid="targeted-title-input"
              />
              <p className="text-[10px] text-[#9B9B9B] mt-0.5 text-end">{targetedForm.title.length}/65</p>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#3D3D3D] mb-1.5">نص الإشعار</label>
              <textarea
                value={targetedForm.body}
                onChange={(e) => setTargetedForm({ ...targetedForm, body: e.target.value })}
                placeholder="اكتب رسالة للمستخدمين المحددين..."
                maxLength={200}
                rows={3}
                className="w-full rounded-xl border border-[#EAEAEA] px-3 py-2 text-sm bg-white outline-none focus:border-[#E05D3A] resize-none"
                data-testid="targeted-body-input"
              />
              <p className="text-[10px] text-[#9B9B9B] mt-0.5 text-end">{targetedForm.body.length}/200</p>
            </div>

            <Button
              onClick={sendTargetedNotification}
              disabled={targetedSending || selectedTargets.length === 0 || !targetedForm.title.trim() || !targetedForm.body.trim()}
              className="w-full bg-[#E05D3A] hover:bg-[#C94D2C] text-white font-bold rounded-full h-11"
              data-testid="send-targeted-btn"
            >
              {targetedSending ? (
                <><Loader2 className="w-4 h-4 animate-spin me-2" /> جاري الإرسال...</>
              ) : (
                <><Send className="w-4 h-4 me-2" /> إرسال الإشعار {selectedTargets.length > 0 ? `إلى ${selectedTargets.length} مستخدم` : "للمستخدمين المحددين"}</>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="jh-card p-6 mt-6" data-testid="push-diagnostics">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#E05D3A]" />
            <h2 className="font-bold text-lg text-[#1A1A1A]">تشخيص إشعارات Push</h2>
            {pushStatus && (
              <span
                className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${
                  pushStatus.fcm_enabled
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
                data-testid="fcm-status-badge"
              >
                {pushStatus.fcm_enabled ? "FCM مفعّل" : "FCM معطّل"}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runTokenProbe}
              data-testid="token-probe-btn"
              className="rounded-full"
            >
              <Activity className="w-4 h-4" />
              <span className="ms-1.5">تشخيص شامل</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshPushStatus}
              disabled={pushLoading}
              data-testid="refresh-push-status-btn"
              className="rounded-full"
            >
              {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ms-1.5">تحديث</span>
            </Button>
          </div>
        </div>
        <p className="text-xs text-[#6B6B6B] mb-4">
          يعرض كل جهاز سجّل توكن Push عند الدخول للتطبيق، مع نتيجة فحص حيّ (Dry-run) للتأكد من صلاحية التوكن.
          إذا كان التوكن غير صالح، لن يصل الإشعار خارج التطبيق على هذا الجهاز.
        </p>
        {pushStatus?.service_account && (
          <div className="rounded-xl bg-[#FAFAFA] border border-[#EAEAEA] p-3 mb-3 text-[11px] font-mono break-all" data-testid="sa-fingerprint">
            <p className="text-[#1A1A1A] font-bold mb-1 not-italic font-sans">بصمة حساب الخدمة (Service Account):</p>
            <p><span className="text-[#6B6B6B]">project_id: </span><span className="text-[#1A1A1A]">{pushStatus.service_account.project_id || "—"}</span></p>
            <p><span className="text-[#6B6B6B]">client_email: </span><span className="text-[#1A1A1A]">{pushStatus.service_account.client_email || "—"}</span></p>
            <p><span className="text-[#6B6B6B]">source: </span><span className="text-[#1A1A1A]">{pushStatus.service_account.source || "—"}</span></p>
          </div>
        )}
        {pushStatus?.fcm_auth_probe && (
          <div
            className={`rounded-xl p-3 mb-4 text-xs ${
              pushStatus.fcm_auth_probe.startsWith("OK")
                ? "bg-green-50 border border-green-200 text-green-800"
                : "bg-red-50 border border-red-200 text-red-800"
            }`}
            data-testid="fcm-auth-probe"
          >
            <p className="font-bold mb-1">
              {pushStatus.fcm_auth_probe.startsWith("OK")
                ? "✓ مصادقة FCM ناجحة — الخادم متصل بـ Firebase بشكل صحيح"
                : "✗ فشل مصادقة FCM — السبب الجذري لعدم وصول الإشعارات"}
            </p>
            <p className="text-[11px] break-all mt-1 opacity-80 font-mono">{pushStatus.fcm_auth_probe}</p>
          </div>
        )}
        {!pushStatus ? (
          <p className="text-sm text-[#9B9B9B] text-center py-6">جاري التحميل...</p>
        ) : pushStatus.total_tokens === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-[#6B6B6B]">لا يوجد أي جهاز مسجّل حالياً</p>
            <p className="text-[11px] text-[#9B9B9B] mt-1">
              افتح التطبيق على الآيفون وسجّل دخول كعميل، ثم اضغط «تحديث».
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pe-1">
            <p className="text-[11px] text-[#9B9B9B] mb-2">
              {pushStatus.total_tokens} جهاز مسجّل
            </p>
            {pushStatus.tokens.map((t, idx) => {
              const valid = t.dry_run === "VALID";
              const pending = !t.dry_run;
              return (
                <div
                  key={idx}
                  className="rounded-xl border border-[#EAEAEA] p-3 flex items-start gap-3"
                  data-testid={`push-token-row-${idx}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {pending ? (
                      <Loader2 className="w-4 h-4 text-[#9B9B9B]" />
                    ) : valid ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-[#1A1A1A] truncate">
                      {t.user || "—"}
                      <span className="ms-2 text-[11px] font-normal text-[#9B9B9B]">
                        ({t.role || "—"} · {t.platform || "?"})
                      </span>
                    </p>
                    <p className="text-[11px] text-[#9B9B9B] font-mono truncate">{t.token_suffix}</p>
                    {t.dry_run && (
                      <p
                        className={`text-[11px] mt-1 ${valid ? "text-green-700" : "text-red-700"} break-all`}
                      >
                        {valid ? "توكن صالح — جاهز لاستقبال الإشعارات" : t.dry_run}
                      </p>
                    )}
                    {t.updated_at && (
                      <p className="text-[10px] text-[#9B9B9B] mt-1">
                        آخر تحديث: {new Date(t.updated_at).toLocaleString("ar-SA", { dateStyle: "short", timeStyle: "short" })}
                      </p>
                    )}
                    {valid && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 h-7 rounded-full text-[11px] px-3"
                        onClick={() => sendTestPush(t.token_suffix)}
                        disabled={testingToken === t.token_suffix}
                        data-testid={`test-push-btn-${idx}`}
                      >
                        {testingToken === t.token_suffix ? (
                          <><Loader2 className="w-3 h-3 me-1.5 animate-spin" /> جاري الإرسال...</>
                        ) : (
                          <><Send className="w-3 h-3 me-1.5" /> أرسل إشعار تجريبي لهذا الجهاز</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
